import { apiFetch } from "./apiFetch";
import { isCapacitorRuntime } from "./appLock";
import { API_BASE_OPERATOR as API_BASE, STORAGE_KEYS } from "../config/vars";
import { playNotificationSound } from "./notificationSound";

function getPlugins() {
    const cap = window?.Capacitor;
    return {
        cap,
        PushNotifications: cap?.Plugins?.PushNotifications,
        LocalNotifications: cap?.Plugins?.LocalNotifications,
        Badge: cap?.Plugins?.Badge || cap?.Plugins?.CapacitorBadge,
        FirebaseMessaging: cap?.Plugins?.FirebaseMessaging,
    };
}

function getNativePushPlatform() {
    try {
        const platform = window?.Capacitor?.getPlatform?.();
        return platform === "ios" ? "ios" : "android";
    } catch (_) {
        return "android";
    }
}

function normalizeTokenValue(payload) {
    if (!payload) return "";
    if (typeof payload === "string") return payload.trim();
    return String(payload?.token || payload?.value || "").trim();
}

function storePushToken(tokenValue) {
    try {
        if (tokenValue) {
            localStorage.setItem(STORAGE_KEYS.FCM_TOKEN_ANDROID, tokenValue);
        }
    } catch (_) {
        // ignore
    }
}

const PENDING_PUSH_OPEN_KEY = "WR_OPERATOR_PENDING_PUSH_OPEN";

function normalizePushKind(value) {
    const raw = String(value || "").trim().toLowerCase();
    if (!raw) return "";

    if (["news", "news_item", "announcement"].includes(raw)) return "news";
    if (["operator_info", "operator-info", "info", "information"].includes(raw)) return "operator_info";
    if (["chat", "message", "messages"].includes(raw)) return "chat";
    if (["poll", "polls", "survey"].includes(raw)) return "poll";
    if (["request", "requests", "request_response", "request_reply"].includes(raw)) return "request";
    if (["access_request", "client_access_request", "access-request", "client-access-request"].includes(raw)) {
        return "access_request";
    }

    return raw;
}

function parseJsonObject(value) {
    if (typeof value !== "string") return null;

    const trimmed = value.trim();
    if (!trimmed || (trimmed[0] !== "{" && trimmed[0] !== "[")) {
        return null;
    }

    try {
        const parsed = JSON.parse(trimmed);
        return parsed && typeof parsed === "object" ? parsed : null;
    } catch (_) {
        return null;
    }
}

function parsePushDeepLink(url) {
    if (!url) return null;

    try {
        const parsedUrl = new URL(String(url));
        if (parsedUrl.protocol !== "wra-operator:") return null;
        if (parsedUrl.hostname !== "push") return null;

        return normalizePushPayload({
            kind: parsedUrl.searchParams.get("kind"),
            news_id: parsedUrl.searchParams.get("news_id"),
            info_id: parsedUrl.searchParams.get("info_id"),
            head_id: parsedUrl.searchParams.get("head_id"),
            poll_id: parsedUrl.searchParams.get("poll_id"),
            request_id: parsedUrl.searchParams.get("request_id"),
            access_request_id: parsedUrl.searchParams.get("access_request_id"),
        });
    } catch (_) {
        return null;
    }
}

function normalizePushPayload(payload) {
    const source = payload && typeof payload === "object" ? payload : {};
    const pick = (...keys) => {
        for (const key of keys) {
            const value = source?.[key];
            if (value === undefined || value === null) continue;
            if (typeof value === "object") continue;
            const normalized = String(value).trim();
            if (normalized) return normalized;
        }
        return "";
    };

    let kind = normalizePushKind(pick("kind", "type", "notification_kind", "category"));
    const news_id = pick("news_id", "newsId");
    const info_id = pick("info_id", "operator_info_id", "operatorInfoId");
    const head_id = pick("head_id", "headId", "chat_id", "chatId", "conversation_id", "conversationId");
    const poll_id = pick("poll_id", "pollId");
    const request_id = pick("request_id", "requestId");
    const access_request_id = pick("access_request_id", "accessRequestId", "client_access_request_id");

    if (!kind) {
        if (news_id) kind = "news";
        else if (info_id) kind = "operator_info";
        else if (head_id) kind = "chat";
        else if (poll_id) kind = "poll";
        else if (request_id) kind = "request";
        else if (access_request_id) kind = "access_request";
    }

    if (!kind) return null;

    const out = { kind };
    if (news_id) out.news_id = news_id;
    if (info_id) out.info_id = info_id;
    if (head_id) out.head_id = head_id;
    if (poll_id) out.poll_id = poll_id;
    if (request_id) out.request_id = request_id;
    if (access_request_id) out.access_request_id = access_request_id;
    return out;
}

function buildPushDeepLinkUrl(parsed) {
    if (!parsed?.kind) return "";

    const params = new URLSearchParams();
    params.set("kind", parsed.kind);
    Object.entries(parsed).forEach(([key, value]) => {
        if (key === "kind" || value === undefined || value === null) return;
        params.set(key, String(value));
    });

    return `wra-operator://push?${params.toString()}`;
}

function extractNotificationPayload(action) {
    const candidates = [
        action?.notification?.data,
        action?.notification?.extra,
        action?.notification?.attachments,
        action?.notification,
        action?.data,
        action?.extra,
        action,
    ];

    for (const candidate of candidates) {
        const parsedCandidate = parseJsonObject(candidate);
        if (parsedCandidate) {
            return parsedCandidate;
        }

        if (candidate && typeof candidate === "object") {
            const nestedPayloads = [candidate.data, candidate.extra, candidate.payload, candidate.notification];
            for (const nested of nestedPayloads) {
                const parsedNested = parseJsonObject(nested);
                if (parsedNested) {
                    return parsedNested;
                }

                if (nested && typeof nested === "object") {
                    return nested;
                }
            }
            return candidate;
        }
    }

    return {};
}

function persistPendingPushOpen(parsed) {
    if (!parsed) return;

    try {
        localStorage.setItem(PENDING_PUSH_OPEN_KEY, JSON.stringify(parsed));
    } catch (_) {
        // ignore
    }

    try {
        window.dispatchEvent(new Event("wrPushOpen"));
    } catch (_) {
        // ignore
    }
}

function handleNotificationAction(action) {
    const extra = extractNotificationPayload(action);
    const parsedFromLink = parsePushDeepLink(
        extra?.deep_link ||
        extra?.deeplink ||
        extra?.url ||
        extra?.link ||
        extra?.click_action
    );
    const parsedPayload = normalizePushPayload(extra) || parsedFromLink;

    try {
        localStorage.setItem(STORAGE_KEYS.LAST_PUSH, JSON.stringify(extra || {}));
    } catch (_) {
        // ignore
    }

    persistPendingPushOpen(parsedPayload);
}

async function postTokenToBackend(tokenValue) {
    if (!API_BASE) return;
    const token = (tokenValue || "").trim();
    if (!token) return;

    try {
        const lastSent = localStorage.getItem(STORAGE_KEYS.FCM_TOKEN_ANDROID_SENT);
        if (lastSent === token) return;
    } catch (_) {
        // ignore
    }

    const res = await apiFetch(`${API_BASE}/push/register`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, platform: getNativePushPlatform() }),
    });

    if (res.ok) {
        try {
            localStorage.setItem(STORAGE_KEYS.FCM_TOKEN_ANDROID_SENT, token);
        } catch (_) {
            // ignore
        }
    }
}

export async function registerStoredPushToken() {
    if (!isCapacitorRuntime()) return;
    try {
        const token = localStorage.getItem(STORAGE_KEYS.FCM_TOKEN_ANDROID);
        if (token) {
            await postTokenToBackend(token);
        }
    } catch (_) {
        // ignore
    }
}

export async function initPushNotifications() {
    if (!isCapacitorRuntime()) return;

    const { PushNotifications, LocalNotifications, Badge, FirebaseMessaging } = getPlugins();
    if (!PushNotifications) {
        console.warn("[Push] PushNotifications plugin not found");
        return;
    }

    try {
        // Android notification channel for sound/heads-up notifications
        if (LocalNotifications?.createChannel) {
            await LocalNotifications.createChannel({
                id: "wr_alerts_v2",
                name: "Сповіщення",
                description: "WorkRestart notifications",
                importance: 5,
                sound: "default",
                visibility: 1,
                lights: true,
                vibration: true,
            });
        }
    } catch (_) {
        // ignore
    }

    try {
        // Ask LocalNotifications permission too (Android 13+)
        try {
            if (LocalNotifications?.checkPermissions && LocalNotifications?.requestPermissions) {
                const lp = await LocalNotifications.checkPermissions();
                if (lp?.display !== "granted") {
                    await LocalNotifications.requestPermissions();
                }
            }
        } catch (_) {
            // ignore
        }

        const perm = await PushNotifications.checkPermissions();
        if (perm?.receive !== "granted") {
            const req = await PushNotifications.requestPermissions();
            if (req?.receive !== "granted") {
                console.warn("[Push] notification permission not granted");
            }
        }

        PushNotifications.addListener("registration", async (t) => {
            const tokenValue = normalizeTokenValue(t);
            console.info(
                "[Push] registered token",
                tokenValue ? tokenValue.slice(0, 12) + "…" : "(empty)"
            );

            // Store token immediately so we can retry /push/register after login.
            storePushToken(tokenValue);

            await postTokenToBackend(tokenValue);
        });

        if (FirebaseMessaging?.addListener) {
            FirebaseMessaging.addListener("tokenReceived", async (event) => {
                const tokenValue = normalizeTokenValue(event);
                if (!tokenValue) return;
                console.info("[Push] firebase token", tokenValue.slice(0, 12) + "…");
                storePushToken(tokenValue);
                await postTokenToBackend(tokenValue);
            });
        }

        PushNotifications.addListener("registrationError", (err) => {
            console.warn("[Push] registrationError", err);
        });

        // Foreground: show it in the tray + play sound via LocalNotifications
        PushNotifications.addListener("pushNotificationReceived", async (n) => {
            try {
                const extra = n?.data || {};
                const title = n?.title || extra?.title || "WorkRestart";
                const body = n?.body || extra?.body || "";
                const parsedPayload = normalizePushPayload(extra);
                const extraWithLink = { ...extra };
                const deepLink = buildPushDeepLinkUrl(parsedPayload);
                if (deepLink) {
                    extraWithLink.deep_link = deepLink;
                }

                try {
                    playNotificationSound();
                    window.dispatchEvent(new CustomEvent("wrPushReceived", { detail: extraWithLink }));
                } catch (_) {
                    // ignore
                }

                if (LocalNotifications?.schedule) {
                    await LocalNotifications.schedule({
                        notifications: [
                            {
                                id: Math.floor(Date.now() / 1000),
                                title,
                                body,
                                extra: extraWithLink,
                                channelId: "wr_alerts_v2",
                                schedule: { at: new Date(Date.now() + 250) },
                                sound: "default",
                            },
                        ],
                    });
                }

                try {
                    if (Badge?.increase) {
                        await Badge.increase();
                    } else if (Badge?.get && Badge?.set) {
                        const cur = await Badge.get();
                        const next = Math.max(0, Number(cur?.count) || 0) + 1;
                        await Badge.set({ count: next });
                    } else if (Badge?.set) {
                        await Badge.set({ count: 1 });
                    }
                } catch (_) {
                    // ignore
                }
            } catch (_) {
                // ignore
            }
        });

        PushNotifications.addListener("pushNotificationActionPerformed", (action) => {
            handleNotificationAction(action);
        });

        if (LocalNotifications?.addListener) {
            LocalNotifications.addListener("localNotificationActionPerformed", (action) => {
                handleNotificationAction(action);
            });
        }

        await PushNotifications.register();
        console.info("[Push] register() called");

        if (getNativePushPlatform() === "ios" && FirebaseMessaging?.getToken) {
            try {
                if (FirebaseMessaging.requestPermissions) {
                    await FirebaseMessaging.requestPermissions();
                }
                const tokenResult = await FirebaseMessaging.getToken();
                const tokenValue = normalizeTokenValue(tokenResult);
                if (tokenValue) {
                    storePushToken(tokenValue);
                    await postTokenToBackend(tokenValue);
                }
            } catch (err) {
                console.warn("[Push] FirebaseMessaging.getToken failed", err);
            }
        }

        // If we already have a token stored (e.g. previous run), try registering it.
        await registerStoredPushToken();
    } catch (e) {
        console.warn("[Push] init failed", e);
    }
}
